import { parseNetworkError } from "../../photos/api/network";
import { extractError } from "../../photos/hooks/userejection";
import { IEventDispatch } from "./dispatch";

/**
 * A FetchFunction is a function that executes the fetch network request and
 * returns the response. It SHOULD include a unique name that will be used in
 * telemetry to identify the action.
 *
 * The supplied function will be given the request options as the first parameter
 * followed by all parameters supplied by the caller.
 */
export type FetchFunction<T, P extends any[]> = (fetch: (url: string, init?: RequestInit) => Promise<Response>, ...args: P) => Promise<T>;

/**
 * The fetchComplete makes the IFetchCompleteEvent object available in the event.
 */
export interface IFetchCompleteEvent {
  /**
   * The length of time the request took to execute in milliseconds.
   */
  duration: number;

  /**
   * The endTime of the request.
   */
  endTime: number;

  /**
   * When a network request is made the service returns a
   * Micorosft Correlation Vector (ms-cv). This can be used to debug requests
   * across distibuted systems. With this id you should be able to find the
   * corresponding telemetry for the service handling.
   */
  ms_cv?: string;

  /**
   * The name of the method called for this request.
   */
  name: string;

  /**
   * The resolved options be used for this fetch operation. This the combination
   * of the user supplied options and the defaults when not supplied.
   */
  options?: IFetchOptions;

  /**
   * Optional data that is shared through the lifecycle of the request as a scratch pad.
   * This data is available to the event handlers and this can be used to
   * provide custom behaviors where needed.
   */
  readonly requestData: any;

  /**
   * The result of the fetch request.
   */
  result: PromiseSettledResult<any>;

  /**
   * Sharepoint server returns 'sprequestguid' response header.
   * This is needed to pull sharepoint server logs for the request
   */
  spRequestGuid?: string;

  /**
   * The start time of the request.
   */
  startTime: number;

  /**
   * The URL being used to execute the request.
   */
  url: string;
}

/**
 * The fetchPrepare makes the IFetchPrepareEvent object available in the event.
 */
export interface IFetchPrepareEvent {
  /**
   * init makes the request init object available to the listener. The listener can
   * then use this event to update the init with its required state.
   */
  init: RequestInit;

  /**
   * The name of the method called for this request.
   */
  name: string;

  /**
   * The fetch options supplied to the fetch function when it was setup.
   */
  options?: IFetchOptions;

  /**
   * Optional data that is shared through the lifecycle of the request as a scratch pad.
   * This data is available to the event handlers and this can be used to
   * provide custom behaviors where needed.
   */
  readonly requestData?: any;

  /**
   * Name of the scenario this call is being requested for. Scenario names
   * are not required and one may not be available.
   */
  scenarioName?: string;

  /**
   * The URL being used to execute the request.
   */
  url: string;

  /**
   * waitUntil can be called and given a promise. This promise signals to the fetch
   * call that the call is not ready and should wait until the supplied promise is
   * complete before executing.
   *
   * @param promise a promise that must complete before the fetch call can be made.
   * @param tag optional tag for diagnotics when waiting for an async completion.
   */
  waitUntil: (promise: Promise<void>, tag?: string) => void;
}

/**
 * The fetchStart makes the IFetchStartEvent object available in the event.
 */
export interface IFetchStartEvent {
  /**
   * The name of the method called for this request.
   */
  name: string;

  /**
   * The fetch options supplied to the fetch function when it was setup.
   */
  options?: IFetchOptions;

  /**
   * The URL being used to execute the request.
   */
  url: string;
}

/**
 * When using the createRequest hook you can options that affect how the fetch
 * operation handles specfic circumstances.
 */
export interface IFetchOptions {
  /**
   * The default options prevent duplicate requests from being executed at the same time.
   * This means that if the fetch function is called twice with the same arguments
   * while the first request is active, it will return the results of the current
   * request with the same arguments.
   *
   * This uses reference equality to determine duplicates, so two calls with
   * different objects with the same values are still different. This is similar
   * to how React dependency lists work.
   *
   * @default false
   */
  allowDuplicate?: boolean;

  /**
   * Optional data the caller can pass through the request. This data is available to the
   * event handlers and this can be used to provide custom behaviors where needed.
   */
  readonly data?: any;

  /**
   * The caller can supply an event dispatch which is used to notify you when requests
   * are being prepared and completed. If an eventDispatch isn't supplied the hooks
   * can't be used to participate in the fetch processing.
   */
  eventDispatch?: IEventDispatch;

  /**
   * A custom fetch function can be supplied and will be used to execute the
   * the fetch call. If one is not supplied window.fetch will be used. When
   * calling from the service worker you MUST supply self.fetch or another
   * implementation since window.fetch is not available.
   *
   * @default window.fetch
   */
  fetch?: (url: string, init?: RequestInit) => Promise<Response>;

  /**
   * Name that is used to record information related to this fetch call. If no name
   * is supplied a static name will be used. Due to minification using the fetch
   * functions name isn't useful as they are minified.
   *
   * @default __unnammed__
   */
  name?: string;

  /**
   * This defines whether or not the fetch call is coming from the main thread
   * or the service worker.
   *
   * @default false
   */
  serviceWorker?: boolean;

  /**
   * Name of the scenario this call is being requested for.
   */
  scenarioName?: string;

  /**
   * Requests can optionally be created with a timeout. This will cause the
   * network request to be aborted after the specified timeout.
   */
  timeoutMs?: number;
}

export function createRequest<T, P extends any[]>(fetchFunction: FetchFunction<T, P>, options?: IFetchOptions): (...args: P) => Promise<T> {
  const activeRequests: Array<{ args: P; fetchPromise: Promise<T> }> = [];

  return (...args: P): Promise<T> => {
    const { allowDuplicate = false, eventDispatch, fetch = window.fetch, name = "__unnamed__", scenarioName, timeoutMs } = options || {};

    let abortController: AbortController | undefined;

    // If the caller requested duplicate requests be removed we need to look for
    // an in process request that matches the incoming request.
    if (!allowDuplicate) {
      for (let activeIndex = 0; activeIndex < activeRequests.length; activeIndex++) {
        const activeRequest = activeRequests[activeIndex];

        // They must have the same number of arguments to be equal.
        if (args.length === activeRequest.args.length) {
          let duplicate = true;

          // Compare each of the arguments for reference equality.
          for (let argumentIndex = 0; argumentIndex < activeRequest.args.length; argumentIndex++) {
            if (args[argumentIndex] !== activeRequest.args[argumentIndex]) {
              duplicate = false;
              break;
            }
          }

          if (duplicate) {
            return activeRequest.fetchPromise;
          }
        }
      }
    }

    // If a timeout was specified, we will use an AbortController to abort the
    // request after the timeout has past.
    if (timeoutMs) {
      abortController = new AbortController();
      window.setTimeout(() => abortController?.abort(), timeoutMs);
    }

    // Execute the supplied function with the localFetch delegate.
    // We perform this within a local promise context to get through the then/catch/finally
    // all before the return to the caller. This lets us track the time taken in
    // the network call without capturing the returnee's time.
    const fetchPromise = new Promise<T>((resolve, reject) => {
      const completeEventData: { [key: string]: string | number | boolean } = {};
      const startTime = Date.now();
      let result: PromiseSettledResult<T>;
      let resolvedUrl: string;

      // This object is shared across events that are fired from this fetch request
      // and is used for event listeners to implement custom behaviors
      const requestData: any = {};

      fetchFunction(localFetch, ...args)
        .then((value: T) => {
          result = { status: "fulfilled", value };

          completeRequest();
          resolve(value);
        })
        .catch((reason: any) => {
          if ((typeof Response === "object" || typeof Response === "function") && reason instanceof Response) {
            return parseNetworkError(reason).then((networkError) => {
              result = { reason: networkError, status: "rejected" };

              completeRequest();
              reject(reason);
            });
          } else {
            result = { reason, status: "rejected" };

            completeRequest();
            reject(reason);
          }
        });

      // CompleteRequest should be called to finish up telemetry, handle parallel
      // request processing, and any other cleanup.
      function completeRequest() {
        const endTime = Date.now();
        const fetchCompleteEvent: IFetchCompleteEvent = {
          ...completeEventData,
          requestData,
          duration: endTime - startTime,
          endTime,
          name,
          options,
          result,
          startTime,
          url: resolvedUrl
        };

        eventDispatch?.dispatchEvent("fetchComplete", fetchCompleteEvent);

        // Remove the completed request from the set of active requests.
        activeRequests.splice(
          activeRequests.findIndex((activeRequest) => activeRequest.fetchPromise === fetchPromise),
          1
        );
      }

      function localFetch(url: string, init?: RequestInit): Promise<Response> {
        const _init: RequestInit = init || {};
        const pendingPromises: Promise<void>[] = [];
        const event: IFetchPrepareEvent = {
          requestData,
          init: _init,
          name,
          options,
          scenarioName,
          url,
          waitUntil: (promise: Promise<void>) => pendingPromises.push(promise)
        };

        // We will start with the initial url supplied by the function. This may
        // be updated through the preparation process, but we want to ensure it
        // has a value in completion telemetry.
        resolvedUrl = event.url;

        // Prepare the request init object and dispatch it through the fetchPrepare event.
        eventDispatch?.dispatchEvent("fetchPrepare", event);

        // Wait for the pending promises to resolve before making calling the fetch function.
        return Promise.all(pendingPromises)
          .catch((reason) => {
            // We failed to resolve the preparation so the call will not be made,
            // but we want to record as many details as possible about the
            // failure, including the current state or the URL resolution.
            resolvedUrl = event.url;

            return Promise.reject({ error: { code: "preparationFailed", message: extractError(reason) } });
          })
          .then(() => {
            // fetchPrepare may have updated the url and we need to use the updated value.
            resolvedUrl = event.url;

            eventDispatch?.dispatchEvent("fetchStart", {
              name,
              options,
              url: resolvedUrl
            });

            // Execute the fetch request.
            const fetchPromise = fetch(resolvedUrl, { signal: abortController?.signal, ..._init }).then((response) => {
              // Clear any abortController that was setup.
              abortController = undefined;

              // Add the correlation vector if one is available.
              const ms_cv = response.headers.get("ms-cv");
              if (ms_cv) {
                completeEventData.ms_cv = ms_cv;
              }

              // Add sprequestguid if available. This is needed to pull sharepoint server logs for the request
              const spRequestGuid = response.headers.get("sprequestguid");
              if (spRequestGuid) {
                completeEventData.spRequestGuid = spRequestGuid;
              }

              return response;
            });

            return fetchPromise;
          });
      }
    });

    // Add the resulting promise to our pending result map when we are latest only.
    // This allows us to ignore results from earlier calls that complete after the
    // new call has started.
    if (!allowDuplicate) {
      activeRequests.push({ args, fetchPromise });
    }

    return fetchPromise;
  };
}
